import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

export function formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
  return new Date(dateStr).toLocaleDateString('en-US', options ?? defaultOptions)
}

export function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(dateStr)
}

export function getDaysRemaining(expiryDate: string): number {
  const now = new Date()
  const expiry = new Date(expiryDate)
  const diffMs = expiry.getTime() - now.getTime()
  return Math.max(0, Math.ceil(diffMs / 86_400_000))
}

export function getTrialPercentage(createdAt: string, trialEndsAt: string): number {
  const created = new Date(createdAt).getTime()
  const ends = new Date(trialEndsAt).getTime()
  const now = Date.now()
  const total = ends - created
  const elapsed = now - created
  return Math.min(100, Math.max(0, (elapsed / total) * 100))
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 3) + '...'
}

export function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text)
}

export function getStatusColor(status: string): string {
  const map: Record<string, string> = {
    active: 'success',
    paid: 'success',
    fulfilled: 'success',
    approved: 'success',
    won: 'success',
    draft: 'muted',
    pending: 'warning',
    authorized: 'warning',
    partially_fulfilled: 'warning',
    unfulfilled: 'warning',
    open: 'warning',
    archived: 'muted',
    refunded: 'error',
    voided: 'error',
    expired: 'error',
    cancelled: 'error',
    lost: 'error',
    rejected: 'error',
    high: 'error',
    medium: 'warning',
    low: 'success',
    trial: 'info',
  }
  return map[status] ?? 'muted'
}

export function shopifyGidToId(gid: string): string {
  return gid.split('/').pop() ?? gid
}

export function buildShopifyGid(resource: string, id: string): string {
  return `gid://shopify/${resource}/${id}`
}

export function isValidShopifyUrl(url: string): boolean {
  return /^[a-zA-Z0-9-]+\.myshopify\.com$/.test(url)
}

export function normalizeShopifyUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase()
}

export function calculateProfit(costPrice: number, retailPrice: number): {
  profit: number
  margin: number
  markup: number
} {
  const profit = retailPrice - costPrice
  const margin = costPrice > 0 ? (profit / retailPrice) * 100 : 0
  const markup = costPrice > 0 ? (profit / costPrice) * 100 : 0
  return { profit, margin, markup }
}

export function suggestRetailPrice(
  costPrice: number,
  storeType: string
): { min: number; max: number; suggested: number; compareAt: number } {
  const multipliers: Record<string, { min: number; max: number }> = {
    dropshipping: { min: 2.5, max: 5 },
    pod: { min: 2, max: 4 },
    physical: { min: 2, max: 3.5 },
    digital: { min: 5, max: 20 },
    services: { min: 3, max: 10 },
    wholesale: { min: 1.5, max: 2.5 },
    hybrid: { min: 2, max: 4 },
  }
  const { min, max } = multipliers[storeType] ?? { min: 2, max: 4 }
  const suggested = costPrice * ((min + max) / 2)
  const compareAt = suggested * 1.5
  return {
    min: costPrice * min,
    max: costPrice * max,
    suggested: Math.ceil(suggested * 100) / 100,
    compareAt: Math.ceil(compareAt * 100) / 100,
  }
}
